import { api } from '@verifime/api-definition';
import { useEffect, useState } from 'react';
import { toDisplayableCapture } from './toDisplayableCapture';

export type TUseFaceCaptureParams = {
  customerId?: string;
  /**
   * Whether to fetch at all. The card sets this only for the levels that have a capture worth
   * showing, so a customer whose face check never ran costs nothing.
   */
  enabled?: boolean;
  /** Called once if the call fails. This package has no logging surface of its own. */
  onError?: (error: unknown) => void;
};

export type TUseFaceCapture = {
  /** The face capture still. Absent until the call lands, and if it fails or returns nothing. */
  src?: string;
};

/**
 * The face capture still, on its own, for the biometric card's thumbnail.
 *
 * **Deliberately not `useBiometricEvidence`.** That hook gathers four endpoints for the evidence
 * dialog and is gated on the dialog being open, because most customers a client opens are never
 * clicked through to. The card is the opposite case: it is always on screen, so it must not drag
 * the other three calls with it. The thumbnail needs one field from one endpoint, and this fetches
 * exactly that.
 *
 * **Absence is a state, not an error.** No capture returns an absent `src`, not a broken tile and
 * not a fetch failure (a real failure reaches the caller through `onError` instead). The card is
 * what turns that absence into `EvidenceThumbnail`'s own "No capture yet" placeholder, deliberately
 * not a missing image, because a card that shows a missing image implies a capture exists and
 * failed, which is a different claim from the customer not having done the check.
 *
 * **`src` is never the raw `portraitImageUrl`.** The endpoint returns a presigned S3 URL, and the
 * portal's `img-src` policy allows `blob:` but not that bucket host (`HeadWithContentSecurityPolicy`
 * must not change to accommodate it, see verifime-portal-web#2047). `toDisplayableCapture` fetches it
 * and hands back a `blob:` object URL instead, the same technique `PortraitDisplay` already uses for
 * the page this one replaces. A capture that cannot be fetched (expired, 404) falls back to the raw
 * URL unchanged, so `EvidenceThumbnail`'s existing `onError` path still fires exactly as it did
 * before this hook fetched anything.
 */
export function useFaceCapture({
  customerId,
  enabled = true,
  onError,
}: TUseFaceCaptureParams): TUseFaceCapture {
  const [capture, setCapture] = useState<TUseFaceCapture>({});

  useEffect(() => {
    // Cleared before anything else, including on the early return. Without this the previous
    // customer's face stays on screen under the new customer's name while the new call is in
    // flight, which is the same leak `useBiometricEvidence` guards against and is worse here,
    // because the card is on screen for every customer rather than behind a click.
    setCapture({});

    if (!customerId || !enabled) {
      return;
    }

    let abandoned = false;
    // The object URL this run creates, so the cleanup below can revoke it. Stays a no-op release
    // when there was nothing to convert or the fetch fell back to the raw URL: see
    // `toDisplayableCapture`.
    let release = () => {};

    api
      .getV2personCustomerIdlivenesstransactionlatest({ params: { customerId } })
      .then((response) => toDisplayableCapture(response?.portraitImageUrl))
      .then((displayable) => {
        if (abandoned) {
          // The customer changed, or this ran on an unmounted component, while the fetch was in
          // flight. Nothing should hold the blob open for a capture nobody is displaying.
          displayable.release();
          return;
        }
        release = displayable.release;
        setCapture({ src: displayable.src });
      })
      .catch((error) => {
        if (!abandoned) {
          onError?.(error);
        }
      });

    return () => {
      abandoned = true;
      release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, enabled]);

  return capture;
}
